@if (loading()) {
  <div class="flex items-center justify-center h-full">
    <mat-spinner diameter="40" />
  </div>
} @else {
  <div class="container flex flex-col mx-auto px-4 md:px-6 pt-8 pb-4">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6 min-h-10">
      <button class="inline-flex!" mat-icon-button (click)="navigateBack()">
        <mat-icon>arrow_back</mat-icon>
      </button>

      <h2 class="text-2xl font-medium">{{ pageTitle() }}</h2>

      <div class="ml-auto flex items-center gap-2">
        @if (mode() === 'edit') {
          <button mat-button (click)="handleResetPassword()">
            <mat-icon>lock_reset</mat-icon>
            Wachtwoord resetten
          </button>
        }

        <button mat-button [disabled]="form.invalid || saving()" (click)="handleSave()">
          <mat-icon>save</mat-icon>
          Opslaan
        </button>
      </div>
    </div>

    <!-- Single column layout -->
    <div class="flex flex-col gap-6">
      <!-- User details card -->
      <div class="bg-white rounded-lg">
        <div class="flex items-center px-6 h-14 border-b border-gray-200">
          <h3 class="text-lg font-medium text-gray-700">Gebruiker details</h3>
        </div>

        <form [formGroup]="form" class="flex flex-col gap-4 p-6">
          @if (isSelfEdit()) {
            <div
              class="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800"
            >
              <mat-icon
                class="text-blue-500 text-lg/4.5! size-4.5! font-medium! flex-none"
                >info</mat-icon
              >
              <span>
                Je kunt je eigen rol en superuser-status niet wijzigen. Vraag een andere
                beheerder om dit te doen.
              </span>
            </div>
          }
          <div class="space-y-2">
            <label for="displayName" class="block font-medium text-sm select-none">
              Weergavenaam
            </label>
            <mat-form-field class="w-full">
              <input
                id="displayName"
                #displayName
                matInput
                formControlName="displayName"
                placeholder="Vul een naam in"
              />
              <mat-hint align="end">{{ displayName.value.length }}/64</mat-hint>

              @if (form.controls.displayName.hasError('required')) {
                <mat-error>Vul een naam in</mat-error>
              }
              @if (form.controls.displayName.hasError('minlength')) {
                <mat-error>
                  Minimaal
                  {{ form.controls.displayName.getError('minlength').requiredLength }}
                  karakters
                </mat-error>
              }
              @if (form.controls.displayName.hasError('maxlength')) {
                <mat-error
                  >Maximaal
                  {{ form.controls.displayName.getError('maxlength').requiredLength }}
                  karakters
                </mat-error>
              }
            </mat-form-field>
          </div>

          <div class="space-y-2">
            <label for="userRole" class="block font-medium text-sm select-none">
              Rol
            </label>
            <mat-form-field class="w-full">
              <mat-select
                id="userRole"
                formControlName="userRoleId"
                placeholder="Selecteer een rol"
                (selectionChange)="handleRoleChange($event.value)"
              >
                @for (role of roles(); track role._id) {
                  <mat-option [value]="role._id">{{ role.label }}</mat-option>
                }
              </mat-select>

              @if (form.controls.userRoleId.hasError('required')) {
                <mat-error>Selecteer een rol</mat-error>
              }
            </mat-form-field>
            @if (!canReadRoles()) {
              <div class="text-xs text-gray-500">
                Om een rol te kiezen heb je het recht "{{ roleReadLabel }}" nodig.
              </div>
            }
          </div>

          <div class="bg-gray-50 border border-gray-200 rounded p-4 pl-2">
            <mat-checkbox [formControl]="isSuperUserControl" class="-mt-1">
              <div class="flex flex-col mt-2">
                <span class="font-medium">Superuser</span>
                <span class="text-xs text-gray-500">
                  Een superuser heeft automatische alle rechten en omzeilt alle
                  rolgebaseerde toegangscontrole.
                </span>
              </div>
            </mat-checkbox>
          </div>
        </form>
      </div>

      <!-- Permissions matrix card -->
      <div class="bg-white rounded-lg">
        <div
          class="flex items-center justify-between px-6 py-3 border-b border-gray-200"
        >
          <div class="flex items-center gap-2">
            <h3 class="text-lg font-medium text-gray-700">Rechten matrix</h3>
          </div>

          @if (hasRoleSelected() && canReadRoles()) {
            <div
              class="inline-flex items-center border border-(--mat-sys-outline) rounded-sm p-1 h-12"
            >
              <button
                class="inline-flex items-center justify-center h-full whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:cursor-pointer focus-visible:ring-gray-600 rounded-xs px-2 h-8 text-xs font-medium"
                (click)="expandAll()"
                type="button"
              >
                Alles uitklappen
              </button>

              <div class="w-px h-4 bg-(--mat-sys-outline) mx-1"></div>

              <button
                class="inline-flex items-center justify-center h-full whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:cursor-pointer focus-visible:ring-gray-600 rounded-xs px-2 h-8 text-xs font-medium"
                (click)="collapseAll()"
                type="button"
              >
                Alles inklappen
              </button>
            </div>
          }
        </div>

        @if (!hasRoleSelected()) {
          <div class="flex flex-col items-center justify-center py-20 text-center">
            <div
              class="size-20 flex items-center justify-center rounded-full mx-auto bg-gray-100 text-gray-400"
            >
              <mat-icon>shield</mat-icon>
            </div>

            <div class="text-sm text-gray-500 py-4 text-center">
              Selecteer een rol om de bijbehorende rechten te bekijken.
            </div>
          </div>
        } @else if (!canReadRoles()) {
          <div class="flex flex-col items-center justify-center py-20 text-center">
            <div
              class="size-20 flex items-center justify-center rounded-full mx-auto bg-gray-100 text-gray-400"
            >
              <mat-icon>shield</mat-icon>
            </div>

            <div class="text-sm text-gray-500 py-4 text-center">
              Om de rechten van deze rol te bekijken heb je het recht "{{
                roleReadLabel
              }}" nodig.
            </div>
          </div>
        } @else {
          <!-- Subtitle -->
          @if (selectedRole(); as role) {
            <div class="px-6 pt-6 pb-2 text-sm text-gray-500">
              Rechten toegekend aan de
              <strong class="text-gray-700">{{ role.label }}</strong> rol
            </div>
          }

          @if (unmetRequirements().length > 0) {
            <div
              class="mx-6 mb-4 p-3 rounded bg-amber-50 border border-amber-300 text-amber-900 text-xs space-y-1"
            >
              <div class="flex items-center gap-1 font-medium">
                <mat-icon class="text-base/5! size-5! px-0.5 shrink-0 text-amber-600">
                  warning
                </mat-icon>
                Niet alle rechten van deze rol werken:
              </div>
              @for (notice of unmetRequirements(); track notice.message) {
                <div class="pl-7">{{ notice.message }}</div>
              }
            </div>
          }

          <!-- Permissions matrix table -->
          <div class="px-6 pb-6">
            <div class="border border-gray-200 rounded-lg overflow-hidden">
              <table class="w-full border-collapse">
                <thead>
                  <tr class="border-b border-gray-200">
                    <th class="text-left text-sm font-semibold text-gray-500 py-3 px-4">
                      Rechten
                    </th>
                    <th
                      class="text-center text-xs font-semibold text-gray-500 py-3 px-4 w-40 border-l border-gray-200"
                    >
                      @if (selectedRole(); as role) {
                        <div class="flex flex-col items-center gap-0.5">
                          <span class="text-gray-700 normal-case text-sm font-semibold">
                            {{ role.label }}
                          </span>
                          <span class="text-gray-400 normal-case text-xs font-normal">
                            {{ selectedPermissionCount() }}/{{ totalPermissionCount }}
                          </span>
                        </div>
                      }
                    </th>
                  </tr>
                </thead>
                <tbody>
                  @for (
                    group of permissionGroups();
                    track group.key;
                    let lastGroup = $last
                  ) {
                    <!-- Group header row -->
                    <tr
                      class="bg-gray-50 cursor-pointer select-none hover:bg-gray-100 transition-colors border-b border-gray-200"
                      [class.border-b]="!(lastGroup && !isGroupExpanded(group.key))"
                      (click)="toggleGroupExpanded(group.key)"
                    >
                      <td colspan="2" class="py-2.5 px-4">
                        <div class="flex items-center gap-1">
                          <div
                            class="transition-transform"
                            [class]="{
                              '-rotate-90': !isGroupExpanded(group.key),
                            }"
                          >
                            <mat-icon
                              class="flex-none text-gray-500 text-xl/5! py-1 px-0.5"
                            >
                              arrow_drop_down
                            </mat-icon>
                          </div>
                          <span class="text-sm font-semibold text-gray-700">
                            {{ group.label }}
                          </span>
                          <span class="text-xs text-gray-400 ml-1">
                            ({{ groupSelectedCount(group) }}/{{
                              group.permissions.length
                            }}
                            rechten)
                          </span>
                        </div>
                      </td>
                    </tr>

                    <!-- Permission rows (animated expand/collapse) -->
                    <tr>
                      <td colspan="2" class="p-0!">
                        <div
                          [@expandCollapse]="
                            isGroupExpanded(group.key) ? 'expanded' : 'collapsed'
                          "
                        >
                          <table class="w-full border-collapse">
                            @for (
                              permission of group.permissions;
                              track permission.key;
                              let last = $last
                            ) {
                              <tr
                                class="border-b"
                                [class]="
                                  last && lastGroup
                                    ? 'border-transparent'
                                    : last
                                      ? 'border-gray-200'
                                      : 'border-gray-100'
                                "
                              >
                                <td class="py-3 px-4 pl-11">
                                  <div class="flex flex-col">
                                    <span class="text-sm font-medium text-gray-800">
                                      {{ permission.actionLabel }}
                                    </span>
                                    <span class="text-xs text-gray-400">
                                      {{ group.label }}
                                      {{ permission.actionLabel | lowercase }}
                                    </span>
                                  </div>
                                </td>
                                <td
                                  class="py-3 px-4 text-center border-l border-gray-200 w-40"
                                >
                                  @if (isPermissionSelected(permission.value)) {
                                    <mat-icon class="text-green-600! text-xl!">
                                      check
                                    </mat-icon>
                                  } @else {
                                    <span class="text-gray-300 text-lg font-medium">
                                      —
                                    </span>
                                  }
                                </td>
                              </tr>
                            }
                          </table>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }
      </div>
    </div>
  </div>
}
